import React, { useEffect, useState } from 'react';
import { CelebratingEmoji as CelebratingEmojiType } from '../../utils/types';

interface CelebratingEmojiProps {
  celebratingEmoji: CelebratingEmojiType;
  onRemove?: (id: string) => void;
  windowHeight: number;
  windowWidth: number;
}

const CelebratingEmoji: React.FC<CelebratingEmojiProps> = ({ 
  celebratingEmoji, 
  onRemove,
  windowHeight,
  windowWidth
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [bounceOffset, setBounceOffset] = useState(0);
  const [horizontalPosition, setHorizontalPosition] = useState(celebratingEmoji.position.x);
  const [direction] = useState(Math.random() > 0.5 ? 1 : -1); // Random left or right
  const [speed] = useState(0.8 + Math.random() * 1); // Random speed between 0.5-1.5

  useEffect(() => {
    // Start bouncing and horizontal movement animation
    const moveAnimation = () => {
      const elapsed = Date.now() - celebratingEmoji.timestamp;
      const bounceSpeed = 0.008; // Bounce frequency
      const bounceHeight = 30; // Maximum bounce height
      const dampening = Math.max(0.3, 1 - elapsed / 15000); // Gradually reduce bouncing over 15 seconds
      
      const offset = Math.sin(elapsed * bounceSpeed) * bounceHeight * dampening;
      setBounceOffset(Math.abs(offset)); // Always bounce upward from ground

      // Horizontal movement
      const newPosition = horizontalPosition + (direction * speed);
      setHorizontalPosition(newPosition);

      // Check if emoji has reached screen edge
      const edgeBuffer = 20; // Buffer from edge
      
      if (newPosition <= edgeBuffer || newPosition >= windowWidth - edgeBuffer) {
        // Emoji reached edge, start fade out
        setIsVisible(false);
        setTimeout(() => onRemove?.(celebratingEmoji.id), 300); // Quick fade out
        return;
      }
    };

    const animationInterval = setInterval(moveAnimation, 16); // ~60fps

    // Fallback: Remove emoji after 25 seconds if it hasn't reached edge
    const removeTimeout = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onRemove?.(celebratingEmoji.id), 500); // Fade out duration
    }, 25000);

    return () => {
      clearInterval(animationInterval);
      clearTimeout(removeTimeout);
    };
  }, [celebratingEmoji.timestamp, celebratingEmoji.id, onRemove, horizontalPosition, direction, speed, windowWidth]);

  if (!isVisible) {
    return (
      <div
        style={{
          position: 'fixed',
          left: horizontalPosition,
          bottom: bounceOffset,
          transform: 'translateX(-50%)',
          fontSize: '2.5em',
          zIndex: 15,
          opacity: 0,
          transition: 'opacity 0.3s ease-out',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {celebratingEmoji.emoji}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: horizontalPosition,
        bottom: bounceOffset + 10, // 10px from bottom when not bouncing
        transform: 'translateX(-50%)',
        fontSize: '2.5em',
        zIndex: 15,
        opacity: 1,
        transition: 'opacity 0.3s ease-in',
        pointerEvents: 'none',
        userSelect: 'none',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        animation: 'celebrateGlow 2s ease-in-out infinite',
      }}
      onClick={() => {
        // Optional: Add a little extra bounce when clicked
        setBounceOffset(prev => prev + 20);
      }}
    >
      {celebratingEmoji.emoji}
      
      {/* Occasional sparkles */}
      {bounceOffset > 25 && (
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '0.8em',
            opacity: 0.7,
            animation: 'sparkle 0.6s ease-out',
          }}
        >
          ✨
        </div>
      )}

      <style>
        {`
          @keyframes celebrateGlow {
            0%, 100% { filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)) brightness(1); }
            50% { filter: drop-shadow(0 2px 8px rgba(255,215,0,0.6)) brightness(1.2); }
          }
          
          @keyframes sparkle {
            0% { opacity: 0; transform: translateX(-50%) scale(0); }
            50% { opacity: 1; transform: translateX(-50%) scale(1); }
            100% { opacity: 0; transform: translateX(-50%) scale(0) translateY(-20px); }
          }
        `}
      </style>
    </div>
  );
};

export default CelebratingEmoji; 